import { Button, Dimmer, Form, Loader, Message } from '@mairistem/composants';
import {
	type Analytique,
	AnalytiqueSearchInput,
	type Ventilation,
	VentilationSearchInput,
} from '@mairistem/comptabilite';
import { getErrorMessage } from '@mairistem/finances-utils';
import { isNil } from 'lodash';
import React from 'react';
import { toast } from 'react-toastify';
import type Reports from '../../../../interfaces/simulation/reports';
import type Simulation from '../../../../interfaces/simulation/simulation';
import {
	getReports,
	recopieReport,
} from '../../../../services/simulation/simulation';
import ResultatReporteTable from './ResultatReporteTable';

interface RecopieReportProps {
	codeAnalytique: string;
	codeVentilation: string;
}

interface ResultatReporteProps {
	onValidate: (data?: unknown) => void;
	simulation: Simulation;
}

const ResultatReporte = ({ onValidate, simulation }: ResultatReporteProps) => {
	const [error, setError] = React.useState<string>();
	const [loadingData, setLoadingData] = React.useState<boolean>(true);
	const [loadingValidate, setLoadingValidate] = React.useState<boolean>(false);
	const [reports, setReports] = React.useState<Reports>();
	const [values, setValues] = React.useState<RecopieReportProps>();

	const hasAnalytiqueEnabled = React.useMemo(
		() =>
			!simulation?.exercice?.anaExec && simulation?.exercice?.gestionAnalytique,
		[simulation?.exercice?.anaExec, simulation?.exercice?.gestionAnalytique],
	);

	const hasVentilationEnabled = React.useMemo(
		() =>
			simulation?.exercice?.gestionVentilation &&
			!simulation?.exercice?.venExec,
		[simulation?.exercice?.gestionVentilation, simulation?.exercice?.venExec],
	);

	const handleChange = (
		name,
		result: Pick<Analytique, 'code'> | Pick<Ventilation, 'code'> | null,
	) => {
		setValues((old) => ({
			...old,
			[name]: result?.code,
		}));
	};

	const handleFetchReports = async () => {
		try {
			setReports(await getReports(simulation.identifiant));
		} catch (e) {
			setError(
				getErrorMessage(
					e,
					'Erreur lors de la récupération des montants de reports',
				),
			);
		} finally {
			setLoadingData(false);
		}
	};

	const handleValidate = async () => {
		try {
			setLoadingValidate(true);
			await recopieReport(simulation, values);
			onValidate?.();
		} catch (e) {
			toast.error(getErrorMessage(e, 'Erreur lors de la recopie du report'));
		} finally {
			setLoadingValidate(false);
		}
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: handleFetchReports
	React.useEffect(() => {
		handleFetchReports();
	}, []);

	if (!isNil(error)) {
		return (
			<Message negative>
				<Message.Header>Erreur</Message.Header>
				<p>{error}</p>
			</Message>
		);
	}

	return loadingData ? (
		<Dimmer active inverted>
			<Loader size="massive" />
		</Dimmer>
	) : (
		<>
			<ResultatReporteTable reports={reports} />
			<Form>
				{(hasAnalytiqueEnabled || hasVentilationEnabled) && (
					<Form.Field content="Spécifier une répartition :" />
				)}
				<Form.Group widths="equal">
					{hasAnalytiqueEnabled && (
						<Form.Field
							control={AnalytiqueSearchInput}
							identifiantExercice={simulation?.identifiantExercice}
							input={{ 'data-testid': 'analytiqueSearchInput' }}
							label="Analytique"
							name="codeAnalytique"
							onChange={handleChange}
							perPage={5}
							placeholder="HCA par défaut"
							value={values?.codeAnalytique}
						/>
					)}
					{hasVentilationEnabled && (
						<Form.Field
							control={VentilationSearchInput}
							identifiantExercice={simulation?.identifiantExercice}
							input={{ 'data-testid': 'ventilationSearchInput' }}
							label="Ventilation"
							name="codeVentilation"
							onChange={handleChange}
							perPage={5}
							placeholder="HCV par défaut"
							value={values?.codeVentilation}
						/>
					)}
				</Form.Group>
			</Form>
			<div>
				<Button
					content="Reprendre les résultats reportés"
					data-testid="confirmButton"
					disabled={loadingValidate}
					floated="right"
					loading={loadingValidate}
					onClick={handleValidate}
					positive
				/>
			</div>
		</>
	);
};

export default ResultatReporte;
